import React from 'react';
import { codeBoxClass, inputClass, secondaryButtonClass, sectionCardClass, sectionTitleClass, selectClass } from './ui';

const RequiredMark = () => <span className="ml-1 text-rose-500">*</span>;

const SDKAppForm = ({
  app,
  index = 0,
  featureSetIds,
  sdkFeatures,
  sdkDeviceFeatures,
  onTextChange,
  onBooleanChange,
  onOverrideChange,
  onAddOverride,
  apiKeyReadOnly = true,
  titlePrefix = 'App',
}) => (
  <div className={sectionCardClass}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <div>
        <div className={sectionTitleClass}>{titlePrefix} {index + 1}</div>
        <div className="font-mono text-xs text-slate-500">{app.apiKey}</div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">API Key</label>
        {apiKeyReadOnly ? (
          <div className={codeBoxClass}>
            {app.apiKey}
          </div>
        ) : (
          <input
            type="text"
            value={app.apiKey}
            onChange={(event) => onTextChange(index, 'apiKey', event.target.value)}
            className={`${inputClass} font-mono text-xs`}
          />
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Bundle ID<RequiredMark /></label>
        <input
          type="text"
          value={app.bundleId}
          onChange={(event) => onTextChange(index, 'bundleId', event.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Feature Set<RequiredMark /></label>
        <select
          value={app.featureSetId}
          onChange={(event) => onTextChange(index, 'featureSetId', event.target.value)}
          className={selectClass}
        >
          <option value="">Select feature set</option>
          {featureSetIds.map((featureSetId) => (
            <option key={featureSetId} value={featureSetId}>
              {featureSetId}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 pt-7">
        <input
          id={`sdk-enabled-${app.apiKey}`}
          type="checkbox"
          checked={app.enabled}
          onChange={(event) => onBooleanChange(index, 'enabled', event.target.checked)}
        />
        <label className="text-sm text-slate-700" htmlFor={`sdk-enabled-${app.apiKey}`}>
          Enabled
        </label>
      </div>

      <div className="flex items-center gap-2 pt-1 md:pt-7">
        <input
          id={`sdk-collect-measurements-${app.apiKey}`}
          type="checkbox"
          checked={app.collectMeasurements}
          onChange={(event) => onBooleanChange(index, 'collectMeasurements', event.target.checked)}
        />
        <label className="text-sm text-slate-700" htmlFor={`sdk-collect-measurements-${app.apiKey}`}>
          Collect Measurements
        </label>
      </div>

      <div className="flex items-center gap-2 pt-1 md:pt-7">
        <input
          id={`sdk-collect-telemetry-${app.apiKey}`}
          type="checkbox"
          checked={app.collectTelemetry}
          onChange={(event) => onBooleanChange(index, 'collectTelemetry', event.target.checked)}
        />
        <label className="text-sm text-slate-700" htmlFor={`sdk-collect-telemetry-${app.apiKey}`}>
          Collect Telemetry
        </label>
      </div>
    </div>

    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className={sectionTitleClass}>Overrides</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onAddOverride(index, 'add')}
            className={secondaryButtonClass}
          >
            + Add
          </button>
          <button
            type="button"
            onClick={() => onAddOverride(index, 'remove')}
            className={secondaryButtonClass}
          >
            + Remove
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {(app.addOverrides || []).map((override, overrideIndex) => (
          <div
            key={`add-${app.apiKey}-${overrideIndex}`}
            className="grid grid-cols-1 gap-3 md:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center"
          >
            <div className="text-sm text-slate-700">Add</div>
            <select
              value={override.sdkDeviceFeature}
              onChange={(event) => onOverrideChange(index, 'add', overrideIndex, 'sdkDeviceFeature', event.target.value)}
              className={selectClass}
            >
              <option value="">Device feature</option>
              {sdkDeviceFeatures.map((feature) => (
                <option key={feature} value={feature}>
                  {feature}
                </option>
              ))}
            </select>
            <div className="text-sm text-slate-700">to</div>
            <select
              value={override.sdkFeature}
              onChange={(event) => onOverrideChange(index, 'add', overrideIndex, 'sdkFeature', event.target.value)}
              className={selectClass}
            >
              <option value="">SDK feature</option>
              {sdkFeatures.map((feature) => (
                <option key={feature} value={feature}>
                  {feature}
                </option>
              ))}
            </select>
          </div>
        ))}

        {(app.removeOverrides || []).map((override, overrideIndex) => (
          <div
            key={`remove-${app.apiKey}-${overrideIndex}`}
            className="grid grid-cols-1 gap-3 md:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center"
          >
            <div className="text-sm text-slate-700">Remove</div>
            <select
              value={override.sdkDeviceFeature}
              onChange={(event) => onOverrideChange(index, 'remove', overrideIndex, 'sdkDeviceFeature', event.target.value)}
              className={selectClass}
            >
              <option value="">Device feature</option>
              {sdkDeviceFeatures.map((feature) => (
                <option key={feature} value={feature}>
                  {feature}
                </option>
              ))}
            </select>
            <div className="text-sm text-slate-700">from</div>
            <select
              value={override.sdkFeature}
              onChange={(event) => onOverrideChange(index, 'remove', overrideIndex, 'sdkFeature', event.target.value)}
              className={selectClass}
            >
              <option value="">SDK feature</option>
              {sdkFeatures.map((feature) => (
                <option key={feature} value={feature}>
                  {feature}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SDKAppForm;
